/**
 * amphr.js — Core JS primitives for the amphr Shopify theme.
 * Vanilla JS only. Loaded with defer (runs after DOM is ready).
 *
 * Exposes window.amphr = { openDrawer, closeDrawers, observeReveals, $, $$ }
 *
 * API surface:
 *   data-drawer-open="#someId"  — opens the element matching that selector as a drawer
 *   data-close                  — closes all open drawers / overlays
 *   .drawer                     — any element styled as a side drawer (gets class `on` when open)
 *   .overlay                    — any full-page overlay (gets class `on` when open)
 *   .reveal                     — scroll-reveals when entering viewport (gets class `in`)
 *   #amphr-scrim                — created automatically; dims page behind drawers
 *   body.amphr-locked           — added while a drawer is open to prevent body scroll
 *   --header-h                  — CSS custom property set to .amphr-header (or header) height
 *
 * All init functions are idempotent (guarded by data-amphr-bound) and re-run on
 * shopify:section:load so the theme editor keeps working after section re-renders.
 */

(function () {
  'use strict';

  /* ─── 1. Helpers ─────────────────────────────────────────────────────────── */
  var $ = function (s, r) { return (r || document).querySelector(s); };
  var $$ = function (s, r) { return Array.prototype.slice.call((r || document).querySelectorAll(s)); };

  var FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  /* ─── 2. Scrim ───────────────────────────────────────────────────────────── */
  var scrim = $('#amphr-scrim');
  if (!scrim) {
    scrim = document.createElement('div');
    scrim.id = 'amphr-scrim';
    scrim.className = 'scrim';
    document.body.appendChild(scrim);
  }

  /* ─── 2b. Overlays belong at <body> level ────────────────────────────────
     The cart drawer and the search overlay are rendered from the layout, so
     they are already body children. The mobile menu is rendered inside the
     header section instead, and Dawn puts z-index:4 on that section-group
     wrapper — the menu ended up painting *under* the scrim. Measured: raising
     the menu's own z-index to 400 changed nothing, moving the element to
     <body> put it on top, so the drawer was boxed in by its ancestor rather
     than losing a z-index comparison. setBackgroundInert() below makes the
     same assumption, so a menu nested in the header also left that whole
     section reachable by screen readers while the drawer was open. */
  function portalOverlays(root) {
    $$('aside.drawer, .overlay', root).forEach(function (el) {
      if (el.parentElement === document.body) return;
      /* A theme-editor reload re-renders the section with a fresh copy; drop
         the one already parked on <body> so ids stay unique. */
      if (el.id) {
        $$('#' + el.id).forEach(function (twin) {
          if (twin !== el && twin.parentElement === document.body) {
            document.body.removeChild(twin);
          }
        });
      }
      document.body.appendChild(el);
    });
  }
  portalOverlays();

  /* ─── 3. Drawer system (with focus management) ──────────────────────────── */
  var lastFocused = null;

  function setBackgroundInert(drawer, inert) {
    // Drawers/overlays are direct children of <body>; make everything else inert
    Array.prototype.forEach.call(document.body.children, function (el) {
      if (el === drawer || el === scrim || el.contains(drawer)) return;
      if (el.tagName === 'SCRIPT' || el.tagName === 'STYLE' || el.tagName === 'LINK') return;
      if (inert) {
        el.setAttribute('inert', '');
      } else {
        el.removeAttribute('inert');
      }
    });
  }

  function openDrawer(sel) {
    var d = $(sel);
    if (!d) return;
    lastFocused = document.activeElement;
    d.classList.add('on');
    scrim.classList.add('on');
    document.body.classList.add('amphr-locked');
    setBackgroundInert(d, true);
    // Move focus to the first focusable element (usually the close button)
    var target = d.querySelector('[autofocus]') || d.querySelector(FOCUSABLE) || d;
    if (target === d) d.setAttribute('tabindex', '-1');
    target.focus({ preventScroll: true });
  }

  function closeDrawers() {
    var open = $$('.drawer.on, .overlay.on');
    if (!open.length) return;
    open.forEach(function (d) {
      d.classList.remove('on');
      setBackgroundInert(d, false);
    });
    scrim.classList.remove('on');
    document.body.classList.remove('amphr-locked');
    if (lastFocused && document.contains(lastFocused)) {
      lastFocused.focus({ preventScroll: true });
    }
    lastFocused = null;
  }

  /* Delegated click handler for open/close triggers and scrim clicks */
  document.addEventListener('click', function (e) {
    var opener = e.target.closest('[data-drawer-open]');
    if (opener) {
      e.preventDefault();
      openDrawer(opener.getAttribute('data-drawer-open'));
      return;
    }
    if (e.target.closest('[data-close]') || e.target === scrim) {
      closeDrawers();
    }
  });

  /* Escape closes; Tab is trapped inside the open drawer (aria-modal promise) */
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape') {
      closeDrawers();
      return;
    }
    if (e.key !== 'Tab') return;
    var openD = $('.drawer.on') || $('.overlay.on');
    if (!openD) return;
    var focusables = $$(FOCUSABLE, openD).filter(function (el) {
      return el.offsetParent !== null || el === document.activeElement;
    });
    if (!focusables.length) return;
    var first = focusables[0];
    var last = focusables[focusables.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  });

  /* ─── 4. Scroll reveals (idempotent; instant in the theme editor) ───────── */
  var revealIO = null;
  function observeReveals(root) {
    if (window.Shopify && window.Shopify.designMode) {
      $$('.reveal', root || document).forEach(function (el) { el.classList.add('in'); });
      return;
    }
    if (!revealIO) {
      revealIO = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (x) {
            if (x.isIntersecting) {
              x.target.classList.add('in');
              revealIO.unobserve(x.target);
            }
          });
        },
        { threshold: 0.08 }
      );
    }
    $$('.reveal', root || document).forEach(function (el) {
      if (!el.classList.contains('in')) revealIO.observe(el);
    });
  }

  /* ─── 5. Header height CSS variable (rAF-throttled resize) ──────────────── */
  function updateHeaderHeight() {
    var headerEl = $('.amphr-header') || $('header');
    if (!headerEl) return;
    document.documentElement.style.setProperty('--header-h', headerEl.offsetHeight + 'px');
  }
  var rafPending = false;
  window.addEventListener('resize', function () {
    if (rafPending) return;
    rafPending = true;
    requestAnimationFrame(function () {
      rafPending = false;
      updateHeaderHeight();
    });
  });

  /* ─── 5b. Hide the header going down, reveal it coming back up ──────────── */
  function initHeaderReveal() {
    var header = $('.amphr-header') || $('header');
    if (!header || header.dataset.amphrReveal) return;
    header.dataset.amphrReveal = '1';

    var lastY = window.scrollY;
    var ticking = false;
    /* Trackpads emit sub-pixel deltas in both directions — without a deadzone
       the header would flicker on a single gesture. */
    var THRESHOLD = 6;

    /* Taking the header out of the flow would shorten the document by its own
       height and yank the page up. The spacer stands in for it. */
    var spacer = document.createElement('div');
    spacer.className = 'head-spacer';
    header.parentNode.insertBefore(spacer, header.nextSibling);

    /* The point where the header has fully scrolled past. Read live rather than
       cached: the announcement bar above it can be edited in the theme editor,
       and its height moves this line. */
    function floatPoint() {
      return spacer.offsetTop + header.offsetHeight;
    }

    function setFloating(on) {
      if (on === header.classList.contains('head--float')) return;
      spacer.style.height = header.offsetHeight + 'px';
      spacer.classList.toggle('on', on);
      header.classList.toggle('head--float', on);
    }

    function onFrame() {
      ticking = false;
      var y = window.scrollY;
      var delta = y - lastY;

      /* Back above the fold: the header returns to its place in the page and
         forgets it was ever hidden. */
      if (y <= floatPoint()) {
        setFloating(false);
        header.classList.remove('head--hidden');
        lastY = y;
        return;
      }

      if (Math.abs(delta) < THRESHOLD) return;
      lastY = y;

      /* Entering the floating state going down: start off-screen, so the header
         does not blink into view at the moment it detaches. */
      if (!header.classList.contains('head--float')) {
        header.classList.add('head--hidden');
        setFloating(true);
      }

      /* Never hide it out from under something anchored to it: an open mega
         menu, or any drawer (which locks the body anyway). */
      if (document.body.classList.contains('amphr-locked') || $('.mega.open')) {
        header.classList.remove('head--hidden');
        return;
      }

      header.classList.toggle('head--hidden', delta > 0);
    }

    window.addEventListener(
      'scroll',
      function () {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(onFrame);
      },
      { passive: true }
    );

    /* Tabbing into a hidden header must bring it back, or focus lands offscreen. */
    header.addEventListener('focusin', function () {
      header.classList.remove('head--hidden');
    });
  }

  /* ─── 5b. Mobile menu — collapsible parents (delegated) ─────────────────
     The drawer is re-parented to <body> by portalOverlays and re-rendered by
     the theme editor, so this listens on document rather than binding rows. */
  document.addEventListener('click', function (e) {
    var toggle = e.target.closest('[data-mnav-toggle]');
    if (!toggle) return;
    var sub = document.getElementById(toggle.getAttribute('aria-controls'));
    if (!sub) return;
    /* .is-open rather than the hidden attribute: display:none cannot animate,
       and .collapse transitions its grid row from 0fr to 1fr. */
    var open = !sub.classList.contains('is-open');
    sub.classList.toggle('is-open', open);
    toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
  });

  /* ─── 6. Locale panel toggle (delegated) ────────────────────────────────── */
  function closeLocPanels() {
    $$('.locsel .locpanel.open').forEach(function (p) {
      p.classList.remove('open');
      var sel = p.closest('.locsel');
      if (sel) sel.classList.remove('up');
      var trig = sel && sel.querySelector('.loctrig');
      if (trig) trig.setAttribute('aria-expanded', 'false');
    });
  }

  /* The panel drops below its trigger, which is right for the header and wrong
     in the mobile menu, where the trigger sits in the drawer's footer and the
     panel opened straight off the bottom of the screen. The CSS has always had
     a .up variant that flips it above the trigger — nothing ever set the class.
     Measured after opening, so the height is the real one. */
  function placeLocPanel(sel, panel, trig) {
    sel.classList.remove('up');
    var t = trig.getBoundingClientRect();
    var h = panel.offsetHeight;
    var GAP = 16;
    var roomBelow = window.innerHeight - t.bottom;
    if (roomBelow < h + GAP && t.top > roomBelow) sel.classList.add('up');
  }
  document.addEventListener('click', function (e) {
    var trig = e.target.closest('.loctrig');
    if (trig) {
      var sel = trig.closest('.locsel');
      if (!sel) return;
      var panel = sel.querySelector('.locpanel');
      if (!panel) return;
      var isOpen = panel.classList.contains('open');
      closeLocPanels();
      if (!isOpen) {
        panel.classList.add('open');
        trig.setAttribute('aria-expanded', 'true');
        placeLocPanel(sel, panel, trig);
      }
      return;
    }
    if (!e.target.closest('.locsel')) {
      closeLocPanels();
    }
  });

  /* ─── 7. Mega menu — hover + keyboard accessible ────────────────────────── */
  function initMegaMenu(root) {
    var header = $('.amphr-header', root && root.querySelector('.amphr-header') ? root : document);
    var mega = $('#amphr-mega');
    if (!header || !mega || header.dataset.amphrMegaBound) return;
    header.dataset.amphrMegaBound = '1';

    var closeTimer = null;
    var triggers = $$('[data-mega]', header);

    function setOpen(open) {
      mega.classList.toggle('open', open);
      mega.setAttribute('aria-hidden', open ? 'false' : 'true');
      triggers.forEach(function (t) { t.setAttribute('aria-expanded', open ? 'true' : 'false'); });
    }
    function openMega() {
      if (closeTimer) { clearTimeout(closeTimer); closeTimer = null; }
      setOpen(true);
    }
    function scheduleMegaClose() {
      closeTimer = setTimeout(function () {
        setOpen(false);
        closeTimer = null;
      }, 140);
    }

    triggers.forEach(function (el) {
      el.setAttribute('aria-haspopup', 'true');
      el.setAttribute('aria-expanded', 'false');
      el.setAttribute('aria-controls', 'amphr-mega');
      el.addEventListener('mouseenter', openMega);
      el.addEventListener('focus', openMega);
      el.addEventListener('click', function (e) {
        // Toggle on click/Enter for keyboard & touch users
        if (mega.classList.contains('open')) {
          setOpen(false);
        } else {
          e.preventDefault();
          openMega();
        }
      });
    });

    mega.addEventListener('mouseenter', openMega);
    mega.addEventListener('mouseleave', scheduleMegaClose);
    header.addEventListener('mouseleave', scheduleMegaClose);
    // Close when focus leaves both header and mega (keyboard users)
    document.addEventListener('focusin', function (e) {
      if (mega.classList.contains('open') && !header.contains(e.target) && !mega.contains(e.target)) {
        setOpen(false);
      }
    });
    setOpen(false);
  }

  /* ─── 8. PLP density toggle (delegated) ─────────────────────────────────── */
  document.addEventListener('click', function (e) {
    var btn = e.target.closest('#density button[data-cols]');
    if (!btn) return;
    var grid = document.getElementById('plpgrid');
    if (!grid) return;
    var densityEl = btn.closest('#density');
    $$('button[data-cols]', densityEl).forEach(function (b) { b.classList.remove('on'); });
    btn.classList.add('on');
    grid.className = 'grid plpgrid c' + btn.getAttribute('data-cols');
  });

  /* ─── 7b. Nav dropdowns (menu items with children) — hover + keyboard ───── */
  (function () {
    var closeTimer = null;

    function setDrop(drop, open) {
      drop.classList.toggle('open', open);
      var trig = drop.querySelector('.ndtrig');
      if (trig) trig.setAttribute('aria-expanded', open ? 'true' : 'false');
    }
    function closeAllDrops(except) {
      $$('[data-navdrop].open').forEach(function (d) {
        if (d !== except) setDrop(d, false);
      });
    }

    document.addEventListener('click', function (e) {
      var trig = e.target.closest('[data-navdrop] .ndtrig');
      if (trig) {
        var drop = trig.closest('[data-navdrop]');
        var isOpen = drop.classList.contains('open');
        closeAllDrops(drop);
        setDrop(drop, !isOpen);
        return;
      }
      if (!e.target.closest('[data-navdrop]')) closeAllDrops();
    });

    document.addEventListener('mouseover', function (e) {
      var drop = e.target.closest('[data-navdrop]');
      if (drop) {
        if (closeTimer) { clearTimeout(closeTimer); closeTimer = null; }
        closeAllDrops(drop);
        setDrop(drop, true);
      }
    });
    document.addEventListener('mouseout', function (e) {
      var drop = e.target.closest('[data-navdrop]');
      if (drop && !drop.contains(e.relatedTarget)) {
        closeTimer = setTimeout(function () { setDrop(drop, false); }, 140);
      }
    });

    // Keyboard: close when focus leaves; Escape closes
    document.addEventListener('focusin', function (e) {
      var open = $('[data-navdrop].open');
      if (open && !open.contains(e.target)) closeAllDrops();
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') closeAllDrops();
    });
  })();

  /* ─── 8b. PDP sticky bar add button → main add-to-bag (delegated) ───────── */
  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-sticky-add]');
    if (!btn) return;
    var add = document.getElementById('amphr-add');
    if (add && !add.disabled) add.click();
  });

  /* ─── 9. Param-preserving sort (delegated) ──────────────────────────────── */
  document.addEventListener('change', function (e) {
    var sel = e.target.closest('#amphr-sort');
    if (!sel) return;
    var params = new URLSearchParams(window.location.search);
    params.set('sort_by', sel.value);
    params.delete('page');
    window.location.href = window.location.pathname + '?' + params.toString();
  });

  /* ─── 10. Filter option (.fo) checkbox toggle visual (delegated) ────────── */
  document.addEventListener('change', function (e) {
    var checkbox = e.target;
    if (checkbox.type !== 'checkbox') return;
    var label = checkbox.closest('.fo');
    if (!label) return;
    label.classList.toggle('on', checkbox.checked);
  });

  /* ─── 11. PDP — variant selection (idempotent) ──────────────────────────── */
  function initProductForm() {
    var variantDataEl = document.getElementById('amphr-variants');
    if (!variantDataEl) return;

    var form = document.getElementById('amphr-product-form');
    var variantInput = document.getElementById('amphr-variant-id');
    var addBtn = document.getElementById('amphr-add');
    if (!form || !variantInput || !addBtn || form.dataset.amphrBound) return;
    form.dataset.amphrBound = '1';

    var variants;
    try { variants = JSON.parse(variantDataEl.textContent); } catch (e) { return; }
    if (!variants || !variants.length) return;

    /* The add-to-bag button no longer repeats the price; the sticky bar does. */
    var stickyPrice = document.getElementById('amphr-sticky-price');

    function formatMoney(cents) {
      if (window.amphr && typeof window.amphr.formatMoney === 'function') {
        return window.amphr.formatMoney(cents);
      }
      return (cents / 100).toFixed(2);
    }

    function getSelectedOptions() {
      var radios = form.querySelectorAll('input[type="radio"][name^="options"]:checked');
      var opts = [];
      radios.forEach(function (r) { opts.push(r.value); });
      return opts;
    }

    function findVariant(selectedOpts) {
      return variants.find(function (v) {
        var vopts = [v.option1, v.option2, v.option3].filter(function (o) { return o !== null && o !== undefined; });
        if (vopts.length !== selectedOpts.length) return false;
        return selectedOpts.every(function (val, i) { return vopts[i] === val; });
      }) || null;
    }

    function updateActiveClasses(changedInput) {
      var name = changedInput.name;
      form.querySelectorAll('input[name="' + name + '"]').forEach(function (inp) {
        var lbl = inp.closest('label');
        if (lbl) lbl.classList.toggle('on', inp.checked);
      });
    }

    /* The colour swatches are unlabelled circles, so the name beside them is
       the only way to tell which one is selected. Which option is the colour
       one is decided in Liquid — we find the label by walking up from the
       radio that changed rather than re-guessing the option name here. */
    function updateOptionLabel(changedInput) {
      var group = changedInput.closest('.opt');
      var label = group && group.querySelector('#amphr-colour-label');
      if (label) label.textContent = changedInput.value;
    }

    /* Picking a size used to scroll the gallery too: every variant carries the
       colour's featured media, so XS and S resolve to the same image, and the
       page jumped to it as if a colour had been chosen. Only move when the
       image the customer would see actually changes. */
    var shownMediaId = null;
    var initialVariant = findVariant(getSelectedOptions());
    if (initialVariant && initialVariant.featured_media) {
      shownMediaId = initialVariant.featured_media.id;
    }

    function scrollToMedia(variant) {
      if (!variant || !variant.featured_media) return;
      if (variant.featured_media.id === shownMediaId) return;
      shownMediaId = variant.featured_media.id;
      var pim = document.querySelector('.pim[data-media-id="' + shownMediaId + '"]');
      if (pim) pim.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }

    /* Arriving from a colour swatch on a card. The gallery holds every shot of
       every colour in merchandising order, so the chosen colour can sit at the
       far end of the rail — measured on Basic T-Shirt: tile 17 of 17 — and the
       page opened on a different colour, which is what the customer had just
       clicked away from.

       scrollLeft rather than scrollIntoView: the rail is horizontal only on
       phones, and scrollIntoView would drag the whole page down to the tile on
       the desktop grid, where every shot is on screen anyway. */
    function positionRailOnArrival(variant) {
      if (!variant || !variant.featured_media) return;
      if (!new URLSearchParams(window.location.search).has('variant')) return;
      var rail = document.querySelector('[data-pgal-rail]');
      if (!rail || rail.scrollWidth <= rail.clientWidth) return;
      var pim = rail.querySelector('.pim[data-media-id="' + variant.featured_media.id + '"]');
      if (pim) rail.scrollLeft = pim.offsetLeft - rail.offsetLeft;
    }
    positionRailOnArrival(initialVariant);

    /* The address bar has to follow the swatches: without this a reload, a
       share or the back button all resolved to the product's default colour,
       the same page the customer had already rejected. */
    function syncUrl(variant) {
      if (!window.history || !window.history.replaceState) return;
      var url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url.toString());
    }

    form.addEventListener('change', function (e) {
      var input = e.target;
      if (!input.matches('input[type="radio"][name^="options"]')) return;

      updateActiveClasses(input);
      updateOptionLabel(input);

      var variant = findVariant(getSelectedOptions());
      if (!variant) return;

      variantInput.value = variant.id;

      /* innerHTML, not textContent — see setMoney in amphr-cart.js. */
      if (stickyPrice) stickyPrice.innerHTML = formatMoney(variant.price);

      addBtn.disabled = !variant.available;
      addBtn.classList.toggle('unavailable', !variant.available);

      scrollToMedia(variant);
      syncUrl(variant);
    });
  }

  /* ─── 12. PDP — sticky add-to-bag bar visibility (idempotent) ───────────── */
  function initStickyBar() {
    var addBtn = document.getElementById('amphr-add');
    var stickyBar = document.getElementById('amphr-sticky');
    if (!addBtn || !stickyBar || stickyBar.dataset.amphrBound) return;
    stickyBar.dataset.amphrBound = '1';

    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          stickyBar.classList.toggle('show', !entry.isIntersecting);
        });
      },
      { threshold: 0, rootMargin: '0px 0px -60px 0px' }
    );
    io.observe(addBtn);
  }

  /* ─── 13. PDP — related products via Product Recommendations API ────────── */
  function initRelatedRecs() {
    var section = document.querySelector('[data-related-recs]');
    if (!section || section.dataset.amphrBound) return;
    section.dataset.amphrBound = '1';

    var url = section.getAttribute('data-url');
    var grid = section.querySelector('[data-related-grid]');
    if (!url || !grid) return;

    fetch(url)
      .then(function (r) {
        if (!r.ok) throw new Error('recommendations ' + r.status);
        return r.text();
      })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var freshGrid = doc.querySelector('[data-related-grid]');
        // Swap only when the API actually returned recommendations
        if (freshGrid && freshGrid.children.length) {
          grid.replaceChildren.apply(grid, Array.prototype.slice.call(freshGrid.children));
        }
      })
      .catch(function () { /* fallback server render stays */ });
  }

  /* ─── 14. Init + theme editor re-init ───────────────────────────────────── */
  function initAll(root) {
    portalOverlays(root);
    observeReveals(root);
    updateHeaderHeight();
    initHeaderReveal();
    initMegaMenu(root);
    initProductForm();
    initStickyBar();
    initRelatedRecs();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(); });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function (e) {
    initAll(e.target);
  });

  /* ─── 15. Public API ────────────────────────────────────────────────────── */
  window.amphr = {
    openDrawer: openDrawer,
    closeDrawers: closeDrawers,
    observeReveals: observeReveals,
    $: $,
    $$: $$,
  };
})();
